<template>
  <v-app class="app_wrapper no-scroll-bar" id="app">
    <div class="top_safe_area"></div>
    <div v-if="loading" class="height100 d-flex justify-center align-center" style="min-height: 100vh;">
      <v-progress-circular indeterminate color="#EE6090" size="50"></v-progress-circular>
    </div>

    <v-main v-if="!loading" class="main_wrapper no-scroll-bar">
      <router-view />
    </v-main>

    <div
      v-else
      class="height100 d-flex justify-center align-center text-center"
    >
      <vue-loading
        type="bars"
        color="#EE6090"
        :size="{ width: '50px', height: '50px' }"
      />
    </div>

    <v-bottom-navigation
      class="bottom_nav"
      v-if="!loading && bottomNavActive"
      app
      grow
      color="primary"
      v-model="bottomNavValue"
    >
      <template v-for="(menu, index) in menus" :key="menu.title">
        <v-btn
          @click="urlReplace(menu.url)"
          :disabled="menu.isDisabled"
          :value="menu.url"
          height="56"
        >
          <div
            v-if="menu.isImage"
            class="nav-icon"
            :class="{ 'active-icon': String(bottomNavValue) === String(menu.url) }"
            :style="{ '--icon-url': `url(${menu.icon})` }"
          ></div>
          <v-icon v-else size="20">{{ menu.icon }}</v-icon>
          <span style="white-space: nowrap; font-size: 10px;">
            {{ menu.title }}
          </span>
        </v-btn>
      </template>
    </v-bottom-navigation>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import LocalDataService from '@/service/LocalDataServices';
// import { VueLoading } from 'vue-loading-template'; // 必要に応じてコメントアウト解除

// store
import { auth0Module } from '@/store/entityModules/auth0';
import { meModule } from '@/store/entityModules/me';
import { familyModule } from '@/store/entityModules/family';
import { pusherModule } from '@/store/entityModules/pusher';

// model
import { ErrorType } from '@/api/ApiClient';

// utils
import { isVevoEnabledNursery } from '@/utils/vevo';

export default defineComponent({
  name: 'App',

  components: {
    // VueLoading,
  },

  data: () => ({
    loading: false,
    bottomNavValue: '',
  }),

  computed: {
    family() {
      return familyModule.family || null;
    },
    menus() {
      const menus: Array<{
        title: string;
        icon: string;
        url: string;
        isDisabled: boolean;
        isImage?: boolean;
      }> = [
        { title: 'ホーム', icon: 'mdi-home', url: '/home', isDisabled: false },
        { title: '記録', icon: 'mdi-pencil', url: '/record', isDisabled: false },
        { title: '行事予定', icon: 'mdi-calendar', url: '/calendar', isDisabled: false },
        { title: 'MEMORU', icon: 'mdi-multimedia', url: '/memoru', isDisabled: false },
      ];
      if (this.showVevoTab) {
        menus.push({ title: 'VEVO', icon: require('@/assets/vevo/nav_icon.png'), url: '/vevo', isDisabled: false, isImage: true });
      }
      menus.push({ title: '設定', icon: 'mdi-cog', url: '/setting', isDisabled: false });
      return menus;
    },
    // VEVO対象施設に属するchildが1人でも紐づいている場合のみタブを表示する
    showVevoTab(): boolean {
      const children = this.family?.children ?? [];
      return children.some((child) => isVevoEnabledNursery(child.nurseryName));
    },
    bottomNavActive() {
      if (
        this.routePath === '' ||
        this.routePath === '/' ||
        this.routePath === '/signin' ||
        this.routePath === '/callback' ||
        this.routePath === '/chat' ||
        // create-user 削除
        this.routePath === '/create-family' ||
        this.routePath === '/record/day' ||
        this.routePath === '/record/calendar' ||
        this.routePath === '/parent/edit' ||
        this.routePath === '/child/edit' ||
        this.routePath === '/family/edit' ||
        this.routePath === '/maintenance' ||
        this.routePath === '/verify/email' ||
        this.routePath === '/error/email/verify'
      ) {
        return false;
      } else {
        return true;
      }
    },
    routePath(): string {
      return this.$route.path;
    },
  },

  watch: {
    routePath: {
      handler(newPath) {
        // URLパス前方一致で詳細ページ滞在時もVEVOタブにマッピング
        if (newPath && newPath.startsWith('/vevo')) {
          this.bottomNavValue = '/vevo';
        } else {
          this.bottomNavValue = newPath;
        }
      },
      immediate: true
    }
  },

  async created() {
    this.loading = true;
    try {
      if (this.$route.path === '/3dsecure') {
        //console.log(this.$route.query.AccessID);
        console.log(this.$route.query.AccessID);
        console.log(this.$route.query.param);
        return
      }
      await auth0Module.initClient();
      const isAuthenticated = await auth0Module.isAuthenticated();
      
      if (isAuthenticated) {
        await this.isToFirstStep();
      } else {
        try {
          await auth0Module.getTokenSilently();
          await this.isToFirstStep();
        } catch (error: any) {
          const errorMsg = error instanceof Error ? error.message : error;

          // isToFirstStep の中で処理しきれなかった致命的なエラー（NO_TOKEN等）をキャッチ
          if (errorMsg === ErrorType.NO_TOKEN) {
            throw error; 
          }

          if (this.$route.query && this.$route.query.code && this.$route.query.state) {
            try {
              await auth0Module.handleAuth0();
            } catch (innerError) {
              window.location.reload();
            }
          } else if (this.$route.query.error && this.$route.query.error === 'unauthorized') {
            this.$router.replace('/error/email/verify');
          } else {
            await auth0Module.signinProcess();
          }
        }
      }
    } catch (error: any) {
      const errorMsg = error instanceof Error ? error.message : error;
      if (errorMsg === ErrorType.NO_TOKEN) {
        this.loading = false;
        auth0Module.logout();
      } else {
        this.loading = false;
      }
    } finally {
      this.loading = false;
    }
  },

  async mounted() {
    window.receivedChatPush = (chatId: string) => {
      // do something
    };

    if (!familyModule.family && meModule.me) {
      await familyModule.fetchFamilyById({
        familyId: meModule.me.families[0].familyId,
      });
    }
  },

  methods: {
    urlReplace(path: string) {
      localStorage.removeItem("selectedDate");
      let currentChildId: any = this.$route.query.childId;
      
      // 現在のchildIdの有無にかかわらず、VEVOタブを開く場合は強制的に対象施設の一番最後のIDで上書き
      if (path === '/vevo' && this.family && this.family.children && this.family.children.length > 0) {
        const vevoChildren = this.family.children.filter((child) =>
          isVevoEnabledNursery(child.nurseryName),
        );
        if (vevoChildren.length > 0) {
          currentChildId = String(vevoChildren[vevoChildren.length - 1].childId);
        }
      }
      
      if (currentChildId) {
        this.$router.replace({
          path: path,
          query: { childId: currentChildId }
        });
      } else {
        this.$router.replace(path);
      }
    },
    async isCreateFamily() {
      if (meModule.me && meModule.me.families.length > 0) {
        const sessionsToken = await auth0Module.getAuth0Token();
        const parentId = String(meModule.me.parentId);
        LocalDataService.setParentId(parentId);
        LocalDataService.setBeamsTokenAndId(`${sessionsToken},${parentId}`);
        await Promise.all([
          familyModule.fetchFamilyById({
            familyId: meModule.me.families[0].familyId,
          }),
          pusherModule.subscribe(),
        ]);
        
        if (
          this.routePath === '' ||
          this.routePath === '/' ||
          this.routePath === '/signin' ||
          this.routePath === '/callback' ||
          // create-user の条件を削除
          this.routePath === '/create-family'
        ) {
          this.$router.replace('/home');
        }
      } else {
        this.$router.replace('/create-family');
      }
    },
    
    // FIRSTLOGINを検知したら、裏側で自動作成して次に進むロジックに完全書き換え
    async isToFirstStep() {
      try {
        await meModule.fetchMe();
        await this.isCreateFamily();
      } catch (error: any) {
        const errorMsg = error instanceof Error ? error.message : error;
        
        if (errorMsg === ErrorType.FIRSTLOGIN) {
          // ユーザーが存在しない場合、Auth0のプロフィール情報を使って裏で自動作成する
          const getAuth0User = await auth0Module.getAuth0User();
          if (getAuth0User) {
            await meModule.postMe({
              firstName: getAuth0User.family_name ? getAuth0User.family_name : '', // 必須項目の場合へのフォールバック
              lastName: getAuth0User.given_name ? getAuth0User.given_name : '',
              firstNameKana: null,
              lastNameKana: null,
              imageUrl: getAuth0User.picture ? getAuth0User.picture : null,
              birth: null,
              bloodType: null,
              tel: null,
            });
            // ユーザー作成に成功したら、そのまま家族作成フローへ進む
            await this.isCreateFamily();
          } else {
            // Auth0のユーザー情報すら取れなかった場合はログアウト扱いにするなど
            throw new Error(ErrorType.NO_TOKEN);
          }
        } else {
          // それ以外の予期せぬエラーは上に投げる
          throw error;
        }
      }
    },

    appUpdate() {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const registration of registrations) {
            registration.update();
          }
        });
      }
    },
  },
});
</script>

<style lang="scss">
@use './sass/ress.scss';

/* --- 以下、既存のスタイルそのまま --- */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  /* iOS/iPadOSのツールバーによる高さのズレを吸収 */
  height: -webkit-fill-available; 
}
#app, .v-application {
  height: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
}
.v-application {
  background-color: #ffffff !important;
}
.main_wrapper {
  min-height: 100%;
  padding-bottom: 56px;
}
.height100 {
  height: 100%;
}
.no-scroll-bar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scroll-bar::-webkit-scrollbar {
  display: none;
}
.bottom_nav {
  position: fixed !important;
  bottom: env(safe-area-inset-bottom) !important;
  left: 0 !important;
  right: 0 !important;
  width: 100vw !important;
  max-width: 100vw !important;
  margin: 0 !important;
  padding: 0 !important;
  z-index: 100 !important;
  overflow: hidden !important;

  .v-btn {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    max-width: none !important;
    padding: 0 4px !important;
    color: #757575 !important;

    .v-btn__content span {
      font-size: 9px !important;
      letter-spacing: -0.5px;
    }
    &.v-btn--selected {
      background-color: transparent !important;
      color: #EE6090 !important;

      .v-btn__content {
        color: #EE6090 !important;
      }
      .v-btn__overlay {
        background-color: transparent;
      }
    }
  }
}
.nav-icon {
  width: 20px;
  height: 20px;
  background-color: #757575; /* 非選択時の色 */

  /* 画像の不透明な部分を「型抜き」として使う設定 */
  mask-image: var(--icon-url);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;

  /* Safariなどのブラウザ対応（プレフィックス） */
  -webkit-mask-image: var(--icon-url);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
}
.active-icon {
  background-color: #EE6090; /* 選択時の色 */
}
#app {
  -webkit-tap-highlight-color: transparent;
}
:root {
  --app-top-offset: calc(env(safe-area-inset-top) + 0px) !important;
}
.top_safe_area {
  background-color: primary !important;
  height: var(--app-top-offset) !important;
  width: 100% !important;
  position: relative !important;
  z-index: 10 !important;
}
.v-bottom-navigation {
  z-index: 100 !important;
  position: fixed !important;
  bottom: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}
.v-bottom-navigation.bottom_nav {
  position: fixed !important;
  bottom: 0 !important; /* 常に画面の真下に固定 */
  left: 0 !important;
  width: 100% !important;
  z-index: 100 !important;
  background-color: white !important;

  /* iPhoneのホームバー領域を下への「余白（padding）」として吸収する */
  padding-bottom: env(safe-area-inset-bottom) !important;
  height: calc(56px + env(safe-area-inset-bottom)) !important;

  /* 中のボタンの設定 */
  .v-btn {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    max-width: none !important;
    padding: 0 4px !important;
    color: #757575 !important;
    height: 56px !important; 

    .v-btn__content span {
      font-size: 9px !important;
      letter-spacing: -0.5px;
    }
    
    &.v-btn--selected {
      background-color: transparent !important;
      color: #EE6090 !important;

      .v-btn__content {
        color: #EE6090 !important;
      }
      .v-btn__overlay {
        background-color: transparent;
      }
    }
  }
}
.v-overlay-scroll-blocked {
  padding-right: 0 !important;
}
</style>